import { useNavigate } from "react-router-dom";

export const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  green: "#059669",
  bg: "#F7FAF7",
  amber50: "#FFFBEB",
  amber800: "#92400E",
  blue50: "#EFF6FF",
  blue700: "#1D4ED8",
  red: "#EF4444",
  red50: "#FEF2F2",
  red700: "#B91C1C",
};

export const FONT =
  'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif';

export const SESSION_KEY = "miadmin_session_v1";

// ────────── 포맷 ──────────

export function relativeTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
  return formatDate(dateStr);
}

export function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
}

export function formatPhone(phone) {
  if (!phone) return "—";
  const s = String(phone).replace(/\D/g, "");
  if (s.length === 11) return `${s.slice(0, 3)}-${s.slice(3, 7)}-${s.slice(7)}`;
  if (s.length === 10) return `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}`;
  return phone;
}

// ────────── 상태칩 ──────────

export function MatchStatusChip({ status }) {
  const map = {
    "0": { label: "매칭중", bg: C.amber50, fg: C.amber800, dot: C.amber800 },
    "1": { label: "매칭완료", bg: C.blue50, fg: C.blue700, dot: C.blue700 },
    "2": { label: "완료", bg: C.greenBg, fg: C.greenDark, dot: C.greenDark },
  };
  const v =
    map[String(status)] || { label: "—", bg: "#F3F4F6", fg: C.sub, dot: C.sub };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 9999,
        fontSize: 11,
        fontWeight: 700,
        background: v.bg,
        color: v.fg,
      }}
    >
      <span
        style={{ width: 5, height: 5, borderRadius: 9999, background: v.dot }}
      />
      {v.label}
    </span>
  );
}

export function ServiceStatusChip({ status }) {
  const isDone = String(status) === "1";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 9999,
        fontSize: 11,
        fontWeight: 700,
        background: isDone ? C.greenBg : C.amber50,
        color: isDone ? C.greenDark : C.amber800,
      }}
    >
      {isDone ? "답변 완료" : "답변 대기"}
    </span>
  );
}

export function UserTypeChip({ userType }) {
  if (!userType) return null;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 6,
        background: userType === "수리기사" ? C.greenBg : C.blue50,
        color: userType === "수리기사" ? C.greenDark : C.blue700,
      }}
    >
      {userType}
    </span>
  );
}

// ────────── 카드/리스트 ──────────

export function StatCard({ label, value, accent, hint }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ fontSize: 12, color: C.sub, fontWeight: 600 }}>{label}</div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          color: accent || C.ink,
          letterSpacing: "-0.02em",
        }}
      >
        {value?.toLocaleString?.() ?? value ?? 0}
      </div>
      {hint && <div style={{ fontSize: 11, color: C.sub }}>{hint}</div>}
    </div>
  );
}

export function Panel({ title, count, right, children }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: C.ink,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </div>
        {typeof count === "number" && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.greenDark,
              background: C.greenBg,
              padding: "2px 8px",
              borderRadius: 9999,
            }}
          >
            {count}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {right}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>{children}</div>
    </div>
  );
}

export function Row({ left, mid, right, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 0",
        borderBottom: `1px solid ${C.line}`,
        fontSize: 13,
        cursor: onClick ? "pointer" : "default",
        transition: "background 0.12s ease",
      }}
      onMouseEnter={(e) => {
        if (onClick) e.currentTarget.style.background = "#FAFCFA";
      }}
      onMouseLeave={(e) => {
        if (onClick) e.currentTarget.style.background = "";
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          color: C.ink,
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {left}
      </div>
      {mid && (
        <div
          style={{
            color: C.sub,
            fontSize: 12,
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {mid}
        </div>
      )}
      <div style={{ color: C.sub, fontSize: 11, flexShrink: 0 }}>{right}</div>
    </div>
  );
}

export function SkeletonRows({ count = 4 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 10,
            padding: "10px 0",
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <div style={{ flex: 1, height: 14, background: "#F3F4F6", borderRadius: 4 }} />
          <div style={{ width: 60, height: 14, background: "#F3F4F6", borderRadius: 4 }} />
        </div>
      ))}
    </>
  );
}

export function Empty({ label = "데이터가 없습니다." }) {
  return (
    <div
      style={{
        padding: "20px 0",
        color: C.sub,
        fontSize: 13,
        textAlign: "center",
      }}
    >
      {label}
    </div>
  );
}

export function InfoRow({ label, value, mono }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 16,
        padding: "10px 0",
        borderBottom: `1px solid ${C.line}`,
        fontSize: 13,
      }}
    >
      <div
        style={{
          width: 110,
          flexShrink: 0,
          color: C.sub,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          flex: 1,
          color: C.ink,
          fontWeight: 500,
          fontFamily: mono ? "SF Mono, Menlo, Consolas, monospace" : "inherit",
          fontSize: mono ? 12 : 13,
          wordBreak: "break-all",
        }}
      >
        {value === null || value === undefined || value === "" ? (
          <span style={{ color: C.sub }}>—</span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

export function DetailFrame({ title, badge, backTo, onBack, children, actions }) {
  const navigate = useNavigate();
  const handleBack = () => {
    if (onBack) onBack();
    else if (backTo) navigate(backTo);
    else navigate(-1);
  };
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "18px 22px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button type="button" onClick={handleBack} style={ghostBtn}>
          ← 뒤로
        </button>
        <div
          style={{
            fontSize: 16,
            fontWeight: 800,
            color: C.ink,
            letterSpacing: "-0.01em",
            marginLeft: 4,
          }}
        >
          {title}
        </div>
        {badge}
        <div style={{ flex: 1 }} />
        {actions}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>{children}</div>
    </div>
  );
}

// ────────── 검색바 / 필터 ──────────

export function SearchBar({ value, onChange, onSubmit, placeholder = "검색어 입력" }) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
      }}
    >
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          height: 36,
          padding: "0 12px",
          borderRadius: 8,
          border: `1px solid ${C.line}`,
          background: "#fff",
          fontSize: 13,
          color: C.ink,
          outline: "none",
          width: 240,
          fontFamily: "inherit",
        }}
      />
      <button type="submit" style={primaryBtnSm}>
        검색
      </button>
    </form>
  );
}

export function FilterPills({ value, onChange, options }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {options.map((opt) => {
        const active = String(value ?? "") === String(opt.value ?? "");
        return (
          <button
            key={opt.label}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              border: `1px solid ${active ? C.greenDark : C.line}`,
              background: active ? C.greenBg : "#fff",
              color: active ? C.greenDark : C.body,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function Pagination({ page, limit, total, onChange }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (limit || 1)));
  if (totalPages <= 1) return null;
  const cur = Math.max(1, Math.min(totalPages, page || 1));
  const window = 5;
  const start = Math.max(1, cur - Math.floor(window / 2));
  const end = Math.min(totalPages, start + window - 1);
  const realStart = Math.max(1, end - window + 1);
  const pages = [];
  for (let p = realStart; p <= end; p++) pages.push(p);

  const btn = (label, p, disabled, active) => (
    <button
      key={`${label}-${p}`}
      type="button"
      onClick={() => !disabled && onChange(p)}
      disabled={disabled}
      style={{
        minWidth: 34,
        height: 34,
        padding: "0 10px",
        borderRadius: 8,
        border: `1px solid ${active ? C.greenDark : C.line}`,
        background: active ? C.greenDark : "#fff",
        color: active ? "#fff" : disabled ? "#9CA3AF" : C.body,
        fontSize: 12,
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        marginTop: 14,
        display: "flex",
        justifyContent: "center",
        gap: 6,
      }}
    >
      {btn("«", 1, cur === 1)}
      {btn("‹", cur - 1, cur === 1)}
      {pages.map((p) => btn(String(p), p, false, p === cur))}
      {btn("›", cur + 1, cur === totalPages)}
      {btn("»", totalPages, cur === totalPages)}
    </div>
  );
}

// ────────── 입력/버튼 ──────────

export function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 12, color: C.body, fontWeight: 700 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

export const inputStyle = {
  width: "100%",
  height: 46,
  padding: "0 14px",
  borderRadius: 10,
  border: `1px solid ${C.line}`,
  background: "#fff",
  fontSize: 15,
  color: C.ink,
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

export const ghostBtn = {
  padding: "8px 14px",
  borderRadius: 8,
  border: `1px solid ${C.line}`,
  background: "#fff",
  color: C.body,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

export const ghostBtnRed = {
  padding: "8px 14px",
  borderRadius: 8,
  border: `1px solid #FECACA`,
  background: "#FEF2F2",
  color: "#991B1B",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
};

export const primaryBtn = {
  padding: "10px 22px",
  borderRadius: 10,
  border: "none",
  background: C.greenDark,
  color: "#fff",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
};

export const primaryBtnSm = {
  height: 36,
  padding: "0 16px",
  borderRadius: 8,
  border: "none",
  background: C.greenDark,
  color: "#fff",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};

export const dangerBtnSm = {
  height: 36,
  padding: "0 14px",
  borderRadius: 8,
  border: `1px solid #FECACA`,
  background: "#FEF2F2",
  color: "#B91C1C",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};

export function CsvExportButton({ kind, label = "CSV 내보내기" }) {
  return (
    <a
      href={`/api/admin/export/${encodeURIComponent(kind)}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 36,
        padding: "0 14px",
        borderRadius: 8,
        border: `1px solid ${C.line}`,
        background: "#fff",
        color: C.body,
        fontSize: 12,
        fontWeight: 700,
        textDecoration: "none",
      }}
    >
      ⬇ {label}
    </a>
  );
}
